import { useEffect, useMemo, useState } from "react";
import { FileCheck2, ExternalLink, Globe, X, ShieldCheck, AlertTriangle, CalendarDays, Users, BookOpenCheck } from "lucide-react";
import { Spinner } from "./common";
import api from "../lib/api";
import { OfficialLinksGrid, MissionCard } from "../pages/VisasHub";

/* Trip Document Checker — structured 7-outcome visa assessment from the
   visa_requirements database + destination-specific passport-validity check +
   per-transit-country assessment + dual-nationality comparison. Always falls
   back to official government links when no curated record matches. */

export const OUTCOME_META = {
  visa_free: { label: "Visa-free", cls: "bg-emerald-50 text-emerald-800 border-emerald-400", dot: "bg-emerald-500" },
  visa_required: { label: "Visa required", cls: "bg-blue-50 text-blue-800 border-blue-400", dot: "bg-blue-500" },
  eta: { label: "Electronic Travel Authorization", cls: "bg-yellow-50 text-yellow-800 border-yellow-400", dot: "bg-yellow-500" },
  visa_on_arrival: { label: "Visa on arrival", cls: "bg-orange-50 text-orange-800 border-orange-400", dot: "bg-orange-500" },
  evisa: { label: "e-Visa", cls: "bg-purple-50 text-purple-800 border-purple-400", dot: "bg-purple-500" },
  transit_visa_required: { label: "Transit visa required", cls: "bg-rose-50 text-rose-800 border-rose-400", dot: "bg-rose-500" },
  special_conditional: { label: "Special / conditional", cls: "bg-zinc-100 text-zinc-700 border-zinc-400", dot: "bg-zinc-500" },
};

export const PURPOSES = [
  ["tourism", "Tourism"], ["business", "Business"], ["transit", "Transit"], ["study", "Study"],
  ["work", "Work"], ["medical", "Medical"], ["family_visit", "Family Visit"], ["fiance", "Fiancé(e)"], ["other", "Other"],
];

export const PASSPORT_TYPES = [
  ["ordinary", "Ordinary passport"], ["diplomatic", "Diplomatic passport"],
  ["official", "Official / service passport"], ["refugee_document", "Refugee travel document"],
];

export const CONFIDENCE_META = {
  verified: { label: "Verified", cls: "bg-emerald-100 text-emerald-700" },
  high: { label: "High confidence", cls: "bg-sky-100 text-sky-700" },
  unverified: { label: "Unverified — AI draft", cls: "bg-amber-100 text-amber-700" },
};

export const OutcomeBadge = ({ outcome, testid }) => {
  const m = OUTCOME_META[outcome];
  if (!m) return null;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-2 border px-3 py-1.5 font-heading font-700 text-sm ${m.cls}`}>
      <span className={`w-2.5 h-2.5 rounded-full ${m.dot}`} /> {m.label}
    </span>
  );
};

const CountrySelect = ({ label, value, onChange, countries, testid, allowEmpty = "Select a country…" }) => (
  <label className="block flex-1 min-w-[200px]">
    <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="mt-1.5 w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none">
      <option value="">{allowEmpty}</option>
      {countries.map((c) => <option key={c.iso2} value={c.iso2}>{c.name}</option>)}
    </select>
  </label>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
    {children}
  </label>
);

const dateInputCls = "mt-1.5 w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none";

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="py-2 border-b border-zinc-100 last:border-0">
      <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-sm text-zinc-800 mt-0.5">{value}</div>
    </div>
  );
}

function MetadataFooter({ a }) {
  const conf = CONFIDENCE_META[a.confidence] || CONFIDENCE_META.unverified;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4 pt-3 border-t border-zinc-200 text-xs text-muted-foreground" data-testid="assessment-metadata">
      <span className={`px-1.5 py-0.5 font-700 ${conf.cls}`}>{conf.label}</span>
      {a.last_verified && <span>Last verified: <span className="font-600 text-zinc-700">{a.last_verified}</span></span>}
      {a.source && (
        <a href={a.source} target="_blank" rel="noreferrer" data-testid="assessment-source-link"
          className="inline-flex items-center gap-1 font-700 text-primary hover:underline">
          Official source ({a.source_type === "government" ? "government" : a.source_type}) <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}

export function AssessmentCard({ a, title, testid = "assessment-card" }) {
  return (
    <div className="border-2 border-zinc-300 bg-white p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <h3 className="font-heading font-700 text-lg">{title}</h3>
        <OutcomeBadge outcome={a.outcome} testid={`${testid}-outcome`} />
      </div>
      {a.summary && <p className="text-base text-zinc-800 mb-3" data-testid={`${testid}-summary`}>{a.summary}</p>}
      <div className="grid sm:grid-cols-2 gap-x-8">
        <div>
          <DetailRow label="Maximum stay" value={a.max_stay} />
          <DetailRow label="Visa details" value={a.visa_details} />
          {a.ea_name && (
            <div className="py-2 border-b border-zinc-100">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Electronic authorization</div>
              <div className="text-sm text-zinc-800 mt-0.5">
                <span className="font-700">{a.ea_name}</span>
                {a.ea_status && <span className={`ml-2 text-xs font-700 px-1.5 py-0.5 ${a.ea_status === "required" ? "bg-yellow-100 text-yellow-800" : a.ea_status === "expected" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {a.ea_status === "required" ? "Required" : a.ea_status === "expected" ? "Expected — verify" : "Not currently required"}</span>}
                {a.ea_notes && <p className="text-xs text-zinc-600 mt-1">{a.ea_notes}</p>}
                {a.ea_url && <a href={a.ea_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-700 text-primary hover:underline mt-1">Official application site <ExternalLink className="w-3 h-3" /></a>}
              </div>
            </div>
          )}
          <DetailRow label="Passport validity" value={a.passport_validity} />
          <DetailRow label="Blank pages" value={a.blank_pages} />
        </div>
        <div>
          <DetailRow label="Health & vaccinations" value={a.health_notes} />
          <DetailRow label="Customs" value={a.customs_notes} />
          <DetailRow label="Immigration" value={a.immigration_notes} />
          <DetailRow label="Transit notes" value={a.transit_notes} />
          <DetailRow label="Special conditions" value={a.special_conditions} />
        </div>
      </div>
      <MetadataFooter a={a} />
    </div>
  );
}

function PassportCheckCard({ pc }) {
  const tone = pc.status === "ok" ? "border-emerald-400 bg-emerald-50/40" : pc.status === "insufficient" ? "border-rose-400 bg-rose-50/40" : "border-amber-300 bg-amber-50/40";
  return (
    <div className={`border-2 p-5 ${tone}`} data-testid="passport-check-card">
      <div className="flex items-center gap-2 mb-1">
        <BookOpenCheck className="w-5 h-5 text-zinc-700" />
        <h3 className="font-heading font-700 text-base">Passport validity check</h3>
        <span className={`text-xs font-900 tracking-widest px-2 py-0.5 ${pc.status === "ok" ? "bg-emerald-600 text-white" : pc.status === "insufficient" ? "bg-rose-600 text-white" : "bg-amber-500 text-white"}`} data-testid="passport-check-status">
          {pc.status === "ok" ? "APPEARS SUFFICIENT" : pc.status === "insufficient" ? "MAY BE INSUFFICIENT" : "CHECK MANUALLY"}
        </span>
      </div>
      <div className="text-sm text-zinc-800 space-y-0.5">
        <div>Your passport expires: <span className="font-700">{pc.passport_expiry}</span></div>
        {pc.required_until && <div>Must remain valid until at least: <span className="font-700">{pc.required_until}</span>
          {pc.months_required != null && <span className="text-xs text-muted-foreground"> ({pc.months_required > 0 ? `${pc.months_required} months ${pc.calculation_method === "after_entry" ? "after entry" : "after departure"}` : "valid for duration of stay"})</span>}</div>}
        {pc.rule && <div className="text-xs text-zinc-600 mt-1">Rule on file: {pc.rule}</div>}
        {pc.note && <p className="text-sm font-600 mt-2">{pc.note}</p>}
      </div>
    </div>
  );
}

const CHECKLIST = ["Valid passport", "Required passport validity", "Required blank pages", "Visa, if required",
  "Electronic authorization, if applicable", "Proof of onward/return travel if required",
  "Accommodation information", "Travel insurance if required", "Health / vaccination requirements", "Customs requirements"];

export default function DocumentChecker({ countries }) {
  const [passport, setPassport] = useState("");
  const [destination, setDestination] = useState("");
  const [purpose, setPurpose] = useState("tourism");
  const [passportType, setPassportType] = useState("ordinary");
  const [transit, setTransit] = useState([]);
  const [transitPick, setTransitPick] = useState("");
  const [travelDate, setTravelDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [passportExpiry, setPassportExpiry] = useState("");
  const [secondPassport, setSecondPassport] = useState("");
  const [children, setChildren] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!passport || !destination) { setResult(null); return; }
    setLoading(true);
    api.get("/visas/check", { params: {
      passport, destination, purpose, passport_type: passportType,
      transit: transit.length ? transit.join(",") : undefined,
      travel_date: travelDate || undefined, return_date: returnDate || undefined,
      passport_expiry: passportExpiry || undefined,
      second_passport: secondPassport || undefined,
      children: children || undefined,
    } }).then((r) => setResult(r.data)).catch(() => setResult(null)).finally(() => setLoading(false));
  }, [passport, destination, purpose, passportType, transit, travelDate, returnDate, passportExpiry, secondPassport, children]);

  const byIso = useMemo(() => Object.fromEntries(countries.map((c) => [c.iso2, c.name])), [countries]);
  const addTransit = (code) => {
    if (code && !transit.includes(code) && code !== passport && code !== destination && transit.length < 6) {
      setTransit([...transit, code]);
    }
    setTransitPick("");
  };

  const dest = result?.destination;
  const a = result?.assessment;
  return (
    <div data-testid="document-checker">
      <div className="border border-zinc-300 bg-white p-6 mb-6">
        <h2 className="font-heading font-700 text-lg mb-1 flex items-center gap-2"><FileCheck2 className="w-5 h-5 text-[#0e7490]" />Trip Document Checker</h2>
        <p className="text-sm text-muted-foreground mb-5">
          Enter your trip and we'll show the entry requirement on file — visa-free, visa required, e-Visa, visa on arrival,
          electronic authorization, transit visa or special conditions — with the official government links for every country on your route.
        </p>
        <div className="flex flex-wrap gap-4">
          <CountrySelect label="My passport country" value={passport} onChange={setPassport} countries={countries} testid="checker-passport-select" />
          <CountrySelect label="Destination country" value={destination} onChange={setDestination} countries={countries} testid="checker-destination-select" />
          <Field label="Purpose of travel">
            <select value={purpose} onChange={(e) => setPurpose(e.target.value)} data-testid="checker-purpose-select"
              className="mt-1.5 w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none min-w-[160px]">
              {PURPOSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>
        <div className="mt-4">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Transit / layover countries (optional)</span>
          <div className="flex flex-wrap items-center gap-2 mt-1.5" data-testid="checker-transit-chips">
            {transit.map((code) => (
              <span key={code} className="inline-flex items-center gap-1.5 border border-[#0e7490] bg-cyan-50 text-[#0e7490] px-2.5 py-1 text-sm font-600" data-testid={`transit-chip-${code}`}>
                {byIso[code] || code}
                <button onClick={() => setTransit(transit.filter((c) => c !== code))} data-testid={`transit-remove-${code}`} aria-label={`Remove ${byIso[code] || code}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
            <select value={transitPick} onChange={(e) => addTransit(e.target.value)} data-testid="checker-transit-select"
              className="border border-dashed border-zinc-400 bg-white px-3 py-1.5 text-sm text-zinc-600 focus:outline-none">
              <option value="">+ Add transit country…</option>
              {countries.filter((c) => !transit.includes(c.iso2) && c.iso2 !== passport && c.iso2 !== destination)
                .map((c) => <option key={c.iso2} value={c.iso2}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-5 pt-4 border-t border-zinc-200">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Trip details (optional — unlocks passport-validity check)</span>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-2">
            <Field label="Travel date"><input type="date" value={travelDate} onChange={(e) => setTravelDate(e.target.value)} data-testid="checker-travel-date" className={dateInputCls} /></Field>
            <Field label="Return date"><input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} data-testid="checker-return-date" className={dateInputCls} /></Field>
            <Field label="Passport expires"><input type="date" value={passportExpiry} onChange={(e) => setPassportExpiry(e.target.value)} data-testid="checker-passport-expiry" className={dateInputCls} /></Field>
            <Field label="Passport type">
              <select value={passportType} onChange={(e) => setPassportType(e.target.value)} data-testid="checker-passport-type-select" className={dateInputCls}>
                {PASSPORT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <CountrySelect label="Second passport (dual nationals)" value={secondPassport} onChange={setSecondPassport}
              countries={countries.filter((c) => c.iso2 !== passport)} testid="checker-second-passport-select" allowEmpty="None" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm font-600 mt-3 cursor-pointer">
            <input type="checkbox" checked={children} onChange={(e) => setChildren(e.target.checked)} data-testid="checker-children-checkbox" className="w-4 h-4 accent-[#0e7490]" />
            <Users className="w-4 h-4 text-zinc-500" /> Traveling with children under 18
          </label>
        </div>
      </div>

      {loading && <Spinner />}
      {!loading && dest && (
        <div className="space-y-6" data-testid="checker-results">
          {a ? (
            <AssessmentCard a={a} title={`${byIso[passport] || passport} passport → ${dest.name} (${(PURPOSES.find(([v]) => v === purpose) || [])[1] || purpose})`} />
          ) : (
            <div className="border border-zinc-300 bg-zinc-50 p-5 text-sm text-zinc-700" data-testid="assessment-fallback">
              <span className="font-700">No curated requirement record for this exact combination yet.</span> Our requirements
              database is expanding — use the official government links below and the{" "}
              <a href={result.iata_travel_centre_url} target="_blank" rel="noreferrer" className="font-700 text-primary hover:underline">IATA Travel Centre</a>{" "}
              (the same data airlines use) to verify your requirements.
            </div>
          )}

          {result.stay_warning && (
            <div className="border border-amber-400 bg-amber-50 p-4 flex gap-2 text-sm text-amber-900" data-testid="stay-warning">
              <AlertTriangle className="w-5 h-5 shrink-0" /> {result.stay_warning}
            </div>
          )}
          {result.passport_check && <PassportCheckCard pc={result.passport_check} />}

          {result.second_passport_assessment && (
            <div data-testid="second-passport-section">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Dual national — with your {byIso[secondPassport] || secondPassport} passport instead:</div>
              <AssessmentCard a={result.second_passport_assessment} testid="second-assessment-card"
                title={`${byIso[secondPassport] || secondPassport} passport → ${dest.name}`} />
            </div>
          )}
          {secondPassport && !result.second_passport_assessment && (
            <p className="text-xs text-muted-foreground" data-testid="second-passport-no-record">
              No curated record yet for a {byIso[secondPassport] || secondPassport} passport to {dest.name} — check the official links below for that nationality too.
            </p>
          )}

          {result.children_note && (
            <div className="border border-sky-300 bg-sky-50 p-4 text-sm text-sky-900 flex gap-2" data-testid="children-note">
              <Users className="w-5 h-5 shrink-0" /> {result.children_note}
            </div>
          )}

          <div className="border border-zinc-300 bg-white p-6">
            <h3 className="font-heading font-700 text-lg mb-1">Entering {dest.name} — official sources</h3>
            <p className="text-sm text-zinc-700 mb-3">{dest.summary}</p>
            <OfficialLinksGrid links={dest.links} />
          </div>

          {(result.transit_countries || []).map((tc) => (
            <div key={tc.iso2} className="border border-amber-300 bg-white p-6" data-testid={`transit-section-${tc.iso2}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <h3 className="font-heading font-700 text-lg">Transiting through {tc.name}</h3>
                {tc.assessment
                  ? <OutcomeBadge outcome={tc.assessment.outcome} testid={`transit-outcome-${tc.iso2}`} />
                  : <span className="inline-flex items-center gap-1.5 border border-amber-400 bg-amber-50 text-amber-800 px-3 py-1.5 font-heading font-700 text-sm" data-testid={`transit-check-separately-${tc.iso2}`}>
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Check transit requirements separately
                    </span>}
              </div>
              {tc.assessment?.summary && <p className="text-sm text-zinc-800 mb-2">{tc.assessment.summary}</p>}
              {tc.assessment?.visa_details && <p className="text-sm text-zinc-700 mb-2">{tc.assessment.visa_details}</p>}
              {tc.assessment?.transit_notes && <p className="text-sm text-zinc-700 mb-2">{tc.assessment.transit_notes}</p>}
              {tc.assessment && <MetadataFooter a={tc.assessment} />}
              <div className="mt-3">
                <OfficialLinksGrid links={tc.links} testidPrefix={`transit-link-${tc.iso2}`} />
              </div>
              {tc.missions_in_home_country?.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">{tc.name} missions in {result.home?.name || "your country"}</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {tc.missions_in_home_country.slice(0, 4).map((m) => <MissionCard key={m.id} m={m} />)}
                  </div>
                </div>
              )}
              <p className="text-xs text-amber-700 mt-4">Transit rules also depend on whether you leave the international transit area, collect/re-check baggage or change airports — always confirm with {tc.name}'s official sources.</p>
            </div>
          ))}

          <div className="border border-zinc-300 bg-white p-6">
            <h3 className="font-heading font-700 text-base mb-3 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#0e7490]" /> Before you travel — document checklist</h3>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5">
              {CHECKLIST.map((c) => (
                <div key={c} className="flex items-center gap-2 text-sm text-zinc-800"><span className="text-emerald-600 font-900">✓</span> {c}</div>
              ))}
            </div>
          </div>

          {result.destination_missions_in_home_country?.length > 0 && (
            <div>
              <h3 className="font-heading font-700 text-lg mb-3">{dest.name} missions in {result.home?.name || "your country"}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {result.destination_missions_in_home_country.map((m) => <MissionCard key={m.id} m={m} />)}
              </div>
            </div>
          )}

          {result.home_advisory_url && (
            <a href={result.home_advisory_url} target="_blank" rel="noreferrer" data-testid="checker-home-advisory"
              className="inline-flex items-center gap-2 text-sm font-700 text-primary hover:underline">
              <Globe className="w-4 h-4" /> {result.home?.name} travel advisories for its citizens <ExternalLink className="w-3 h-3" />
            </a>
          )}

          <div className="border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="assessment-disclaimer">
            {result.assessment_disclaimer} Verify with the official links above and the{" "}
            <a href={result.iata_travel_centre_url} target="_blank" rel="noreferrer" className="font-700 underline">IATA Travel Centre</a>.
          </div>
        </div>
      )}
    </div>
  );
}
